import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ScrollView, StyleSheet, Text, View } from 'react-native'
import { Color, Font, Spacing } from 'zens-storybook'
import { useNumberParams } from 'zens-router'
import { useBreadcrumb } from '@dms/hook'
import { LayoutHeader } from '@dms/component'

const sections = [
  'What is AI Data Assistant',
  'What problem it solves',
  'How it works',
  'What it can do',
  'What it cannot do',
  'Important to know',
  'Tips and tricks',
  'Examples of user requests',
  'FAQ',
] as const

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100%',
  },
  content: {
    flex: 1,
    marginHorizontal: Spacing.L,
    marginTop: Spacing.S,
    marginBottom: Spacing.L,
  },
  scrollContent: {
    padding: Spacing.L,
    backgroundColor: Color.simplyWhite,
    borderRadius: 8,
  },
  contentsCard: {
    marginBottom: Spacing.L,
    padding: Spacing.M,
    backgroundColor: '#F5F7FA',
    borderRadius: 8,
  },
  contentsTitle: {
    fontFamily: Font.medium,
    fontSize: 18,
    color: Color.premiumBlack,
    marginBottom: Spacing.S,
  },
  contentsLink: {
    fontFamily: Font.light,
    fontSize: 16,
    lineHeight: 12,
    color: '#1F5FAF',
    marginBottom: Spacing.S,
  },
  heading: {
    fontFamily: Font.medium,
    fontSize: 24,
    marginBottom: Spacing.M,
    color: Color.premiumBlack,
  },
  paragraph: {
    fontFamily: Font.light,
    fontSize: 16,
    lineHeight: 24,
    color: Color.premiumBlack,
    marginBottom: Spacing.M,
  },
  inlineLink: {
    color: '#1F5FAF',
    textDecorationLine: 'underline' as const,
  },
  listItem: {
    fontFamily: Font.light,
    fontSize: 16,
    lineHeight: 24,
    color: Color.premiumBlack,
    marginBottom: Spacing.XS,
  },
  faqQuestion: {
    fontStyle: 'italic' as const,
  },
})

const AssistantGuide = () => {
  const { t } = useTranslation()
  const [{ companyId }] = useNumberParams()
  const scrollViewRef = useRef<ScrollView | null>(null)
  const sectionOffsetsRef = useRef<Record<string, number>>({})

  const breadcrumb = useBreadcrumb([
    {
      title: t('AI Data Assistant'),
      href: `/companies/${companyId}/assistant`,
    },
    {
      title: t('User Guide'),
    },
  ])

  const scrollToSection = (section: string) => {
    const y = sectionOffsetsRef.current[section]

    if (y === undefined) return

    scrollViewRef.current?.scrollTo({ y, animated: true })
  }

  const registerSectionOffset = (section: string, y: number) => {
    sectionOffsetsRef.current[section] = y
  }

  return (
    <View style={styles.container}>
      <LayoutHeader title={t('User Guide')} breadcrumb={breadcrumb} />
      <View style={styles.content}>
        <ScrollView ref={scrollViewRef} contentContainerStyle={styles.scrollContent}>
          <View style={styles.contentsCard}>
            <Text style={styles.contentsTitle}>{t('Contents')}</Text>
            {sections.map((section, index) => (
              <Text key={section} style={styles.contentsLink} onPress={() => scrollToSection(section)}>
                {`${index + 1}. ${t(section)}`}
              </Text>
            ))}
          </View>

          <Text style={styles.heading} onLayout={(event) => registerSectionOffset('What is AI Data Assistant', event.nativeEvent.layout.y)}>
            {t('What is AI Data Assistant')}
          </Text>
          <Text style={styles.paragraph}>{t(
            "The AI Data Assistant is software designed to support users in accessing and understanding their company's data through natural language interaction. " +
            "The chatbot acts as an intelligent interface between users and underlying data sources."
          )}</Text>

          <Text style={styles.heading} onLayout={(event) => registerSectionOffset('What problem it solves', event.nativeEvent.layout.y)}>
            {t('What problem it solves')}
          </Text>
          <Text style={styles.paragraph}>{t(
            "The AI Data Assistant makes it possible for users to have full access to the data of" +
            " their company while not requiring any knowledge of databases and technologies to retrieve that data. " +
            "It helps users quickly find and export the data in a report in the layout they want without needing technical expertise or support from data teams. " +
            "Users can get data in granularity, time ranges and combinations that are not available in the DMS interface."
          )}</Text>

          <Text style={styles.heading} onLayout={(event) => registerSectionOffset('How it works', event.nativeEvent.layout.y)}>
            {t('How it works')}
          </Text>
          <View style={{ marginBottom: Spacing.M }}>
            <Text style={styles.listItem}>
              {t("1. You ask a question in your language\n" +
              "No technical skills needed. Just type what you want to know in regard to your company's data.\n" +
              "Check out ")}
              <Text style={styles.inlineLink} onPress={() => scrollToSection('Tips and tricks')}>
                {t('Tips and tricks')}
              </Text>
              {t(" to see how to get the best out of your questions.")}
            </Text>
            <Text style={styles.listItem}>{t(
              "2. The assistant processes your question\n" +
              "It asks for clarifications if needed, then with the help of AI translates your question " +
              "into a structured database query, runs it and returns results instantly."
            )}</Text>
            <Text style={styles.listItem}>{t(
              "3. You receive a downloadable report\n" +
              "Results are delivered in an excel report\n" +
              "The assistant provides explanations of the columns and rows in this report\n" +
              "Preview is available directly in the chat"
            )}</Text>
            <Text style={styles.listItem}>{t(
              "4. Explains the results\n" +
              "You can ask follow-up questions to better understand the data or refine your request."
            )}</Text>
            <Text style={styles.listItem}>{t(
              "5. Keeps your work organized\n" +
              "Saves all chats automatically\n" +
              "Easy overview of past queries\n" +
              "Select, manage, or delete conversations"
            )}</Text>
          </View>

          <Text style={styles.heading} onLayout={(event) => registerSectionOffset('What it can do', event.nativeEvent.layout.y)}>
            {t('What it can do')}
          </Text>
          <Text style={styles.paragraph}>{t(
            "- Retrieve data as requested.\n" +
            "- Present the data in the layout you request. For example, you can ask to have the dates in rows or in columns.\n" +
            "- Explain the data and answer follow-up clarifying questions."
          )}</Text>

          <Text style={styles.heading} onLayout={(event) => registerSectionOffset('What it cannot do', event.nativeEvent.layout.y)}>
            {t('What it cannot do')}
          </Text>
          <Text style={styles.paragraph}>{t(
            "- Provide conclusions or recommendations based on the data.\n" +
            "- Provide information about the employees of the company, but only about the occupancy data generated by them."
          )}</Text>

          <Text style={styles.heading} onLayout={(event) => registerSectionOffset('Important to know', event.nativeEvent.layout.y)}>
            {t('Important to know')}
          </Text>
          <Text style={styles.paragraph}>{t(
            "- The system is designed to answer only occupancy-related questions\n" +
            "- It's primarily a data retrieval tool, not a data analysis tool\n" +
            "- It's not designed to give conclusions or recommendations\n" +
            "- The final output is always an excel report with data in columns and rows\n" +
            "- It stores occupancy data per quarter. You cannot request data for minutes\n" +
            "- By default the system always assumes days Monday-Friday and working hours of your company.\n" + 
            "  If you need data for weekends or outside working hours, you need to specify it in your request."
          )}</Text>

          <Text style={styles.heading} onLayout={(event) => registerSectionOffset('Tips and tricks', event.nativeEvent.layout.y)}>
            {t('Tips and tricks')}
          </Text>
          <View style={{ marginBottom: Spacing.M }}>
            <Text style={styles.listItem}>{t(
              "1. As with all AI tools, be as specific as possible. The less general the request, the less the AI has to guess."
            )}</Text>
            <Text style={styles.listItem}>{t(
              "2. If you know what you want in columns and what in rows of your report, say it to the assistant.\n" +
              "For example: \"Show me average occupancy per workspace per hour on floor 3 for the last month. Show workspaces in rows and hours in columns\"."
            )}</Text>
            <Text style={styles.listItem}>{t(
              "3. The output results should not be more than 4,000,000 rows and 3,000 columns. For example, if you want to see occupancy per quarter-hour in columns, make sure that you don't request more than a month, which would be 96 x 31 = 2,974 columns for a whole day + weekends."
            )}</Text>
            <Text style={styles.listItem}>{t(
              "4. If you want to know about peak occupancy, the system will calculate it by finding the quarter of an hour with the highest occupancy per unit you are requesting (it can be for a floor, a building, a weekday etc.)"
            )}</Text>
            <Text style={styles.listItem}>{t(
              "5. If you want to have a composite column like this 2026-03-02 09:00, or like this 2026-03-02 00:00-00:15, you can use the \"+\" sign like here: \"put date+hour into columns\"."
            )}</Text>
            <Text style={styles.listItem}>{t(
              "6. Use these terms in your requests to help the AI better understand what you want: average occupancy, peak occupancy, quarterly, daily, hourly, weekly, monthly, yearly, workspace or desk, location, building, floor, zone."
            )}</Text>
          </View>

          <Text style={styles.heading} onLayout={(event) => registerSectionOffset('Examples of user requests', event.nativeEvent.layout.y)}>
            {t('Examples of user requests')}
          </Text>
          <Text style={styles.paragraph}>{t(
            "- \"Show me average occupancy per building for the last 30 days.\"\n" +
            "- \"Show me peak occupancy per floor per weekday for the last quarter.\"\n" +
            "- \"Give me occupancy per workspace every 15 minutes for yesterday. Show workspaces in rows and time slots in columns.\"\n" +
            "- \"Compare the average occupancy of Building A and Building B for the last month.\"\n" +
            "- \"Show me daily occupancy for floor 3 for the last 12 weeks.\"\n" +
            "- \"Show me average occupancy of zone Z1 per hour on Mondays and Tuesdays for the last 3 months.\""
          )}</Text>

          <Text style={styles.heading} onLayout={(event) => registerSectionOffset('FAQ', event.nativeEvent.layout.y)}>
            {t('FAQ')}
          </Text>
          <Text style={styles.paragraph}>
            <Text style={styles.faqQuestion}>{t('Q: What kind of questions can I ask?')}</Text>
            {t(
              "\nA: You can ask any question related to workspace and building occupancy data — " +
              "average occupancy, peak occupancy, utilization per floor, zone, building, or workspace, " +
              "filtered by time range, weekday, or hour.\n\n"
            )}
            <Text style={styles.faqQuestion}>{t('Q: Can it answer questions not related to occupancy?')}</Text>
            {t(
              "\nA: No. The assistant is designed exclusively for occupancy-related data queries. " +
              "Questions about weather, general knowledge, or other unrelated topics will not be answered.\n\n"
            )}
            <Text style={styles.faqQuestion}>{t('Q: What is the finest level of detail I can get?')}</Text>
            {t(
              "\nA: The system stores data per quarter of an hour (15-minute intervals). " +
              "You can request data at daily, hourly, or quarter-hour granularity.\n\n"
            )}
            <Text style={styles.faqQuestion}>{t('Q: Can I get data for today?')}</Text>
            {t(
              "\nA: No. Today's data is not yet available. The most recent data is from yesterday.\n\n"
            )}
            <Text style={styles.faqQuestion}>{t('Q: Does the assistant apply working hours and weekday filters by default?')}</Text>
            {t(
              "\nA: Yes. By default, results include only Monday–Friday during your company's configured working hours. " +
              "If you need weekends or hours outside of working hours, specify it explicitly in your request.\n\n"
            )}
            <Text style={styles.faqQuestion}>{t('Q: Are there size limits for the output?')}</Text>
            {t(
              "\nA: Yes. The maximum output is 4,000,000 rows and 3,000 columns. " +
              "If your request would exceed these limits, you will be asked to narrow it down.\n\n"
            )}
            <Text style={styles.faqQuestion}>{t('Q: What happens if my question is unclear?')}</Text>
            {t(
              "\nA: The assistant will ask clarification questions before proceeding. " +
              "It will also confirm its understanding of your request before generating the report.\n\n"
            )}
            <Text style={styles.faqQuestion}>{t('Q: Can the assistant draw conclusions from the data?')}</Text>
            {t(
              "\nA: No. It retrieves and presents data, but does not provide analysis, conclusions, or recommendations.\n\n"
            )}
            <Text style={styles.faqQuestion}>{t('Q: Can I choose how the report is laid out?')}</Text>
            {t(
              "\nA: Yes. You can specify what goes in rows and what goes in columns. " +
              "For example, you can ask for workspaces in rows and hours in columns, or vice versa.\n\n"
            )}
            <Text style={styles.faqQuestion}>{t('Q: Can I get information about employees?')}</Text>
            {t(
              "\nA: No. The system only works with occupancy data generated by sensors. " +
              "It does not store or provide any personal employee information."
            )}
          </Text>
        </ScrollView>
      </View>
    </View>
  )
}

export default AssistantGuide
